import React from 'react';
import { createBrowserRouter, RouterProvider } from 'react-router-dom';
import ErrorBoundary from './components/ErrorBoundary';
import Navigation from './components/Navigation';
import Hero from './components/Hero';
import Features from './components/Features';
import About from './components/About';
import CTASection from './components/CTASection';
import Footer from './components/Footer';
import Pricing from './pages/landing/Pricing';
import AboutPage from './pages/landing/About';
import Contact from './pages/landing/Contact';
import Help from './pages/landing/Help';
import Blog from './pages/landing/Blog';
import Careers from './pages/landing/Careers';
import Community from './pages/landing/Community';
import Docs from './pages/landing/Docs';
import Security from './pages/landing/Security';
import Status from './pages/landing/Status';
import Login from './pages/auth/Login';
import Register from './pages/auth/Register';
import ForgotPassword from './pages/auth/ForgotPassword';
import EmailVerification from './pages/auth/EmailVerification';
import ResetPassword from './pages/auth/ResetPassword';
import TermsOfService from './pages/landing/TermsOfService';
import PrivacyPolicy from './pages/landing/PrivacyPolicy';
import UserDashboard from './pages/userdashboard/UserDashboard';
import AIAssistant from './pages/userdashboard/AIAssistant';
import Wallet from './pages/userdashboard/Wallet';
import MyChamas from './pages/userdashboard/MyChamas';
import CreateChama from './pages/userdashboard/CreateChama';
import Reminders from './pages/userdashboard/Reminders';
import Settings from './pages/userdashboard/Settings';
import SecuritySettings from './pages/userdashboard/SecuritySettings';
import NotificationTone from './pages/userdashboard/NotificationTone';
import PaymentMethods from './pages/userdashboard/PaymentMethods';
import HelpCenter from './pages/userdashboard/HelpCenter';
import ContactSupport from './pages/userdashboard/ContactSupport';
import ChangePassword from './pages/userdashboard/ChangePassword';
import LoginHistory from './pages/userdashboard/LoginHistory';
import Notifications from './pages/userdashboard/Notifications';
import Invitations from './pages/userdashboard/Invitations';
import Profile from './pages/userdashboard/Profile';
import Meetings from './pages/userdashboard/Meetings';
import CreateMeetingUser from './pages/userdashboard/CreateMeeting';
import MeetingSummaryUser from './pages/userdashboard/MeetingSummary';
import PhysicalMeeting from './pages/userdashboard/PhysicalMeeting';
import OnlineMeeting from './pages/userdashboard/OnlineMeeting';
import Chat from './pages/userdashboard/Chat';
import TransactionHistory from './pages/userdashboard/TransactionHistory';
import Deposit from './pages/userdashboard/Deposit';
import Withdraw from './pages/userdashboard/Withdraw';
import Transfer from './pages/userdashboard/Transfer';
import RequestMoney from './pages/userdashboard/RequestMoney';
import ChamaDashboard from './pages/chamadashboard/ChamaDashboard';
import ChamaDetails from './pages/chamadashboard/ChamaDetails';
import ChamaMembers from './pages/chamadashboard/ChamaMembers';
import ChamaContribute from './pages/chamadashboard/ChamaContribute';
import ChamaTransactions from './pages/chamadashboard/ChamaTransactions';
import ChamaLoans from './pages/chamadashboard/ChamaLoans';
import ChamaMeetings from './pages/chamadashboard/ChamaMeetings';
import CreateMeeting from './pages/chamadashboard/CreateMeeting';
import MeetingSummary from './pages/chamadashboard/MeetingSummary';
import PhysicalMeetingAttendance from './pages/chamadashboard/PhysicalMeetingAttendance';
import OnlineMeetingAttendance from './pages/chamadashboard/OnlineMeetingAttendance';
import ChamaMerryGoRound from './pages/chamadashboard/ChamaMerryGoRound';
import ChamaWelfare from './pages/chamadashboard/ChamaWelfare';
import ChamaSharesDividends from './pages/chamadashboard/ChamaSharesDividends';
import ChamaPollsVoting from './pages/chamadashboard/ChamaPollsVoting';
import ChamaAccountManagement from './pages/chamadashboard/ChamaAccountManagement';
import ChamaGroupChat from './pages/chamadashboard/ChamaGroupChat';
import ChamaSettings from './pages/chamadashboard/ChamaSettings';
import AuthTabBar from './components/AuthTabBar';
import UserDashboardTabBar from './components/UserDashboardTabBar';
import ChamaDashboardTabBar from './components/ChamaDashboardTabBar';
import { getThemeColors } from './utils/theme';

// Suppress React Router deprecation warnings for now
const originalWarn = console.warn;
console.warn = (...args) => {
  if (args[0]?.includes?.('React Router Future Flag Warning')) {
    return; // Suppress the specific warning
  }
  originalWarn(...args);
};

const HomePage: React.FC = () => {
  const styles: { [key: string]: React.CSSProperties } = {
    main: {
      paddingTop: '80px', // Account for fixed navigation
      width: '100%',
      overflowX: 'hidden', // Prevent horizontal scroll
    },
  };

  return (
    <main style={styles.main}>
      <div id="home" style={{ width: '100%' }}>
        <Hero />
      </div>
      <div id="features" style={{ width: '100%' }}>
        <Features />
      </div>
      <div id="about-section" style={{ width: '100%' }}>
        <About />
      </div>
      <div style={{ width: '100%' }}>
        <CTASection />
      </div>
    </main>
  );
};

const Layout: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const styles: { [key: string]: React.CSSProperties } = {
    layout: {
      width: '100%',
      minHeight: '100vh',
      overflowX: 'hidden', // Prevent horizontal scroll
    },
  };

  return (
    <div style={styles.layout}>
      <Navigation />
      {children}
      <Footer />
    </div>
  );
};

const router = createBrowserRouter([
  {
    path: "/",
    element: <Layout><HomePage /></Layout>,
  },
  {
    path: "/pricing",
    element: <Layout><Pricing /></Layout>,
  },
  {
    path: "/about",
    element: <Layout><AboutPage /></Layout>,
  },
  {
    path: "/contact",
    element: <Layout><Contact /></Layout>,
  },
  {
    path: "/help",
    element: <Layout><Help /></Layout>,
  },
  {
    path: "/blog",
    element: <Layout><Blog /></Layout>,
  },
  {
    path: "/careers",
    element: <Layout><Careers /></Layout>,
  },
  {
    path: "/community",
    element: <Layout><Community /></Layout>,
  },
  {
    path: "/docs",
    element: <Layout><Docs /></Layout>,
  },
  {
    path: "/security",
    element: <Layout><Security /></Layout>,
  },
  {
    path: "/status",
    element: <Layout><Status /></Layout>,
  },
  {
    path: "/login",
    element: <Login />,
  },
  {
    path: "/register",
    element: <Register />,
  },
  {
    path: "/forgot-password",
    element: <ForgotPassword />,
  },
  {
    path: "/email-verification",
    element: <EmailVerification />,
  },
  {
    path: "/reset-password",
    element: <ResetPassword />,
  },
  {
    path: "/terms-of-service",
    element: <TermsOfService />,
  },
  {
    path: "/privacy-policy",
    element: <PrivacyPolicy />,
  },
  {
    path: "/dashboard",
    element: <UserDashboard />,
  },
  {
    path: "/admin-dashboard",
    element: <div>Admin Dashboard Coming Soon</div>, // Placeholder for admin dashboard
  },
  {
    path: "/dashboard/ai-assistant",
    element: <AIAssistant />,
  },
  {
    path: "/dashboard/wallet",
    element: <Wallet />,
  },
  {
    path: "/dashboard/mychamas",
    element: <MyChamas />,
  },
  {
    path: "/dashboard/create-chama",
    element: <CreateChama />,
  },
  {
    path: "/dashboard/reminders",
    element: <Reminders />,
  },
  {
    path: "/dashboard/settings",
    element: <Settings />,
  },
  {
    path: "/dashboard/settings/security",
    element: <SecuritySettings />,
  },
  {
    path: "/dashboard/settings/notification-tone",
    element: <NotificationTone />,
  },
  {
    path: "/dashboard/settings/payment-methods",
    element: <PaymentMethods />,
  },
  {
    path: "/dashboard/settings/help-center",
    element: <HelpCenter />,
  },
  {
    path: "/dashboard/settings/contact-support",
    element: <ContactSupport />,
  },
  {
    path: "/dashboard/settings/change-password",
    element: <ChangePassword />,
  },
  {
    path: "/dashboard/settings/login-history",
    element: <LoginHistory />,
  },
  {
    path: "/dashboard/profile",
    element: <Profile />,
  },
  {
    path: "/dashboard/notifications",
    element: <Notifications />,
  },
  {
    path: "/dashboard/invitations",
    element: <Invitations />,
  },
  {
    path: "/dashboard/meetings",
    element: <Meetings />,
  },
  {
    path: "/dashboard/meetings/create",
    element: <CreateMeetingUser />,
  },
  {
    path: "/dashboard/meetings/:meetingId/summary",
    element: <MeetingSummaryUser />,
  },
  {
    path: "/dashboard/meetings/:meetingId/physical",
    element: <PhysicalMeeting />,
  },
  {
    path: "/dashboard/meetings/:meetingId/online",
    element: <OnlineMeeting />,
  },
  {
    path: "/dashboard/chat",
    element: <Chat />,
  },
  {
    path: "/dashboard/transactions",
    element: <TransactionHistory />,
  },
  {
    path: "/dashboard/wallet/deposit",
    element: <Deposit />,
  },
  {
    path: "/dashboard/wallet/withdraw",
    element: <Withdraw />,
  },
  {
    path: "/dashboard/wallet/transfer",
    element: <Transfer />,
  },
  {
    path: "/dashboard/wallet/request",
    element: <RequestMoney />,
  },
  {
    path: "/dashboard/chama/:chamaId",
    element: <ChamaDashboard />,
  },
  {
    path: "/dashboard/chama/:chamaId/details",
    element: <ChamaDetails />,
  },
  {
    path: "/dashboard/chama/:chamaId/members",
    element: <ChamaMembers />,
  },
  {
    path: "/dashboard/chama/:chamaId/contribute",
    element: <ChamaContribute />,
  },
  {
    path: "/dashboard/chama/:chamaId/transactions",
    element: <ChamaTransactions />,
  },
  {
    path: "/dashboard/chama/:chamaId/loans",
    element: <ChamaLoans />,
  },
  {
    path: "/dashboard/chama/:chamaId/meetings",
    element: <ChamaMeetings />,
  },
  {
    path: "/dashboard/chama/:chamaId/meetings/create",
    element: <CreateMeeting />,
  },
  {
    path: "/dashboard/chama/:chamaId/meetings/:meetingId/summary",
    element: <MeetingSummary />,
  },
  {
    path: "/dashboard/chama/:chamaId/meetings/:meetingId/attendance/physical",
    element: <PhysicalMeetingAttendance />,
  },
  {
    path: "/dashboard/chama/:chamaId/meetings/:meetingId/attendance/online",
    element: <OnlineMeetingAttendance />,
  },
  {
    path: "/dashboard/chama/:chamaId/merry-go-round",
    element: <ChamaMerryGoRound />,
  },
  {
    path: "/dashboard/chama/:chamaId/welfare",
    element: <ChamaWelfare />,
  },
  {
    path: "/dashboard/chama/:chamaId/shares-dividends",
    element: <ChamaSharesDividends />,
  },
  {
    path: "/dashboard/chama/:chamaId/polls-voting",
    element: <ChamaPollsVoting />,
  },
  {
    path: "/dashboard/chama/:chamaId/account-management",
    element: <ChamaAccountManagement />,
  },
  {
    path: "/dashboard/chama/:chamaId/chat",
    element: <ChamaGroupChat />,
  },
  {
    path: "/dashboard/chama/:chamaId/settings",
    element: <ChamaSettings />,
  },
]);


const App: React.FC = () => {
  const colors = getThemeColors('dark');
  const styles: { [key: string]: React.CSSProperties } = {
    app: {
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", "Oxygen", "Ubuntu", "Cantarell", "Fira Sans", "Droid Sans", "Helvetica Neue", sans-serif',
      WebkitFontSmoothing: 'antialiased',
      MozOsxFontSmoothing: 'grayscale',
      backgroundColor: colors.background,
      color: colors.text,
      minHeight: '100vh',
      width: '100%',
      overflowX: 'hidden', // Prevent horizontal scroll
      margin: 0,
      padding: 0,
    },
  };

  return (
    <ErrorBoundary>
      <div style={styles.app}>
        <RouterProvider router={router} />
        <AuthTabBar />
        <UserDashboardTabBar />
        <ChamaDashboardTabBar activeTab="members" onTabChange={() => {}} />
      </div>
    </ErrorBoundary>
  );
};

export default App;

